"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import posthog from "posthog-js";
import { createSearchParams } from './searchParamsHelper';

type Step = 'location' | 'budget' | 'property-type' | 'possession';
type BuyerType = 'buyer' | 'investor';
type AnimationState = 'entering' | 'visible' | 'exiting';

// These would be based on your actual data
const LOCATIONS = [
  { 
    id: "west", 
    heading: "West Hyderabad", 
    subheading: "Gachibowli, Financial District, Kokapet, Mokila, Tellapur, etc."
  },
  { 
    id: "north", 
    heading: "North Hyderabad", 
    subheading: "Kompally, Miyapur, Bachupally, Kukatpally, Yapral, etc."
  },
  { 
    id: "east", 
    heading: "East Hyderabad", 
    subheading: "Uppal, Pocharam, LB Nagar, Vanasthalipuram, etc."
  },
  { 
    id: "south", 
    heading: "South Hyderabad", 
    subheading: "Shamshabad, Tukkuguda, Mankhal, etc."
  },
  {
    id: 'not-sure',
    heading: "Not sure",
    subheading: "I'll decide later or I need help"
  }
];

const BUDGETS = [
  { 
    id: "budget-1", 
    heading: "Under 1 crore", 
    subheading: "Affordable options"
  },
  { 
    id: "budget-2", 
    heading: "1 Crore - 2 Crores", 
    subheading: "Mid-range properties"
  },
  { 
    id: "budget-3", 
    heading: "2 Crore - 4 Crores", 
    subheading: "Premium options"
  },
  { 
    id: "budget-4", 
    heading: "Over 4 Crores", 
    subheading: "Luxury properties"
  },
];

const PROPERTY_TYPES = [
  { 
    id: "apartment", 
    heading: "Apartment", 
    subheading: "Flats in gated communities"
  },
  { 
    id: "villa", 
    heading: "Villa", 
    subheading: "Independent homes in communities"
  },
];

const POSSESSION_TIMES = [
  { 
    id: "ready", 
    heading: "Ready to Move", 
    subheading: "Available immediately"
  },
  { 
    id: "1year", 
    heading: "Within 1 year", 
    subheading: "Finishing construction"
  },
  { 
    id: "2years", 
    heading: "1-2 years", 
    subheading: "Under construction"
  },
  { 
    id: "3years", 
    heading: "3+ years", 
    subheading: "Early stages"
  },
];

export default function OnboardingPage() {
  const router = useRouter();
  const [buyerType, setBuyerType] = useState<BuyerType | null>(null);
  const [currentStep, setCurrentStep] = useState<Step>('location');
  const [previousStep, setPreviousStep] = useState<Step | null>(null);
  const [animationState, setAnimationState] = useState<AnimationState>('visible');
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [selectedBudget, setSelectedBudget] = useState<string | null>(null);
  const [selectedPropertyType, setSelectedPropertyType] = useState<string | null>(null);
  const [selectedPossession, setSelectedPossession] = useState<string | null>(null);

  useEffect(() => {
    // Instead of checking for the type parameter, we assume everyone is a buyer for now
    setBuyerType('buyer');
  }, []);

  const changeStep = (newStep: Step, isForward: boolean = true) => {
    setPreviousStep(currentStep);
    setDirection(isForward ? 'forward' : 'backward');
    setAnimationState('exiting');
    
    // Wait for exit animation to complete
    setTimeout(() => {
      setCurrentStep(newStep);
      setAnimationState('entering');
      
      // Wait for enter animation to complete
      setTimeout(() => {
        setAnimationState('visible');
      }, 100); // Reduced from 300ms for faster animation
    }, 100); // Reduced from 300ms for faster animation
  };

  const handleLocationSelection = (locationId: string) => {
    setSelectedLocation(locationId);
    posthog.capture("onboarding_location_selected", { locationId, buyerType });
    changeStep('budget');
  };

  const handleBudgetSelection = (budgetId: string) => {
    setSelectedBudget(budgetId);
    posthog.capture("onboarding_budget_selected", { budgetId, buyerType });
    changeStep('property-type');
  };

  const handlePropertyTypeSelection = (typeId: string) => {
    setSelectedPropertyType(typeId);
    posthog.capture("onboarding_property_type_selected", { typeId, buyerType });
    const onboardingData = {
      location: selectedLocation,
      budget: selectedBudget,
      propertyType: typeId,
      buyerType
    };
    localStorage.setItem('onboardingData', JSON.stringify(onboardingData));
    
    // Navigate to search results with the selected filters
    const searchParams = createSearchParams(selectedLocation, selectedBudget, typeId);
    router.push(`/search?${searchParams.toString()}`);
  };

  const handlePossessionSelection = (possessionId: string) => {
    setSelectedPossession(possessionId);
    posthog.capture("onboarding_possession_selected", { possessionId, buyerType });
    
    // Save all selections to localStorage
    const onboardingData = {
      location: selectedLocation,
      budget: selectedBudget,
      propertyType: selectedPropertyType,
      possession: possessionId,
      buyerType
    };
    localStorage.setItem('onboardingData', JSON.stringify(onboardingData));
    
    // Navigate to search results with the selected filters
    const searchParams = createSearchParams(selectedLocation, selectedBudget, selectedPropertyType, possessionId);
    router.push(`/search?${searchParams.toString()}`);
  };

  const handleBackToLocation = () => {
    changeStep('location', false);
  };

  const handleBackToBudget = () => {
    changeStep('budget', false);
  };

  const handleBackToPropertyType = () => {
    changeStep('property-type', false);
  };

  const getAnimationClasses = () => {
    const baseClasses = "transition-all duration-200 ease-in-out"; // Reduced from duration-300
    
    if (animationState === 'entering') {
      return `${baseClasses} ${direction === 'forward' ? 'translate-x-[100%] opacity-0 scale-95' : 'translate-x-[-100%] opacity-0 scale-95'} absolute inset-x-0`;
    }
    
    if (animationState === 'exiting') {
      return `${baseClasses} ${direction === 'forward' ? 'translate-x-[-100%] opacity-0 scale-95' : 'translate-x-[100%] opacity-0 scale-95'} absolute inset-x-0`;
    }
    
    return `${baseClasses} translate-x-0 opacity-100 scale-100 relative`;
  };

  if (!buyerType) {
    return <div className="h-screen flex items-center justify-center">Loading...</div>;
  }

  // Determine which back button to show based on current step
  const renderBackButton = () => {
    if (currentStep === 'location') {
      return (
        <button 
          onClick={() => router.push('/')}
          className="btn btn-outline btn-ghost text-sm mt-4 border border-gray-300"
        >
          Back
        </button>
      );
    } else if (currentStep === 'budget') {
      return (
        <button 
          onClick={handleBackToLocation}
          className="btn btn-outline btn-ghost text-sm mt-4 border border-gray-300"
        >
          Back
        </button>
      );
    } else if (currentStep === 'property-type') {
      return (
        <button 
          onClick={handleBackToBudget}
          className="btn btn-outline btn-ghost text-sm mt-4 border border-gray-300"
        >
          Back
        </button>
      );
    } else {
      return (
        <button 
          onClick={handleBackToPropertyType}
          className="btn btn-outline btn-ghost text-sm mt-4 border border-gray-300"
        >
          Back
        </button>
      );
    }
  };

  return (
    <div className="min-h-screen bg-base-100 py-4 overflow-hidden">
      <div className="container mx-auto px-4 max-w-4xl relative">
        <div className="sm:bg-base-100 bg-white rounded-lg md:shadow-lg p-6 space-y-6">
          {/* Content that animates */}
          <div className="relative" style={{ minHeight: '260px' }}>
            <div className={getAnimationClasses()}>
              {currentStep === 'location' && (
                <>
                  <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">
                    What locations are you looking at?
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {LOCATIONS.map((location) => (
                      <button
                        key={location.id}
                        onClick={() => handleLocationSelection(location.id)}
                        className="btn  btn-primary w-full h-auto py-4 text-sm flex flex-col items-center normal-case"
                      >
                        <span className="font-bold text-base">{location.heading}</span>
                        <span className="text-xs opacity-75 text-center">{location.subheading}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}

              {currentStep === 'budget' && (
                <>
                  <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">
                    What's your budget?
                  </h2>
                  <div className="grid grid-cols-1 gap-4">
                    {BUDGETS.map((budget) => (
                      <button
                        key={budget.id}
                        onClick={() => handleBudgetSelection(budget.id)}
                        className="btn  btn-primary w-full h-auto py-4 flex flex-col items-center normal-case"
                      >
                        <span className="font-bold text-base">{budget.heading}</span>
                        <span className="text-xs opacity-75 text-center">{budget.subheading}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}

              {currentStep === 'property-type' && (
                <>
                  <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">
                    What type of property?
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {PROPERTY_TYPES.map((type) => (
                      <button
                        key={type.id}
                        onClick={() => handlePropertyTypeSelection(type.id)}
                        className="btn  btn-primary w-full h-auto py-4 flex flex-col items-center normal-case"
                      >
                        <span className="font-bold text-base">{type.heading}</span>
                        <span className="text-xs opacity-75 text-center">{type.subheading}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* Clipped possession questions until further notice */}
              {/*
              {currentStep === 'possession' && (
                <>
                  <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">
                    When do you expect possession?
                  </h2>
                  <div className="grid grid-cols-1 gap-4">
                    {POSSESSION_TIMES.map((time) => (
                      <button
                        key={time.id}
                        onClick={() => handlePossessionSelection(time.id)}
                        className="btn  btn-primary w-full h-auto py-4 flex flex-col items-center normal-case"
                      >
                        <span className="font-bold text-base">{time.heading}</span>
                        <span className="text-xs opacity-75 text-center">{time.subheading}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
              */}
            </div>
          </div>

          {/* Static back button */}
          {renderBackButton()}
        </div>
      </div>
    </div>
  );
} 